import React from 'react';
import type { ComponentProps, ReactElement } from 'react';

import PropTypes from 'prop-types';

import {
  useControlled,
  variantSwitch,
  getValidChildren,
} from '@adsk/alloy-react-helpers';
import { useTheme } from '@adsk/alloy-react-styles';
import { stylePropType, StylableComponent } from '@adsk/alloy-react-theme';

import Button from '../Button';

const UNSET: unknown = 'unset';

const VARIANTS = {
  SECONDARY: Button.VARIANTS.SECONDARY,
  TERTIARY: Button.VARIANTS.TERTIARY,
} as const;

const SIZES = {
  SMALL: Button.SIZES.SMALL,
  MEDIUM: Button.SIZES.MEDIUM,
} as const;

/**
 * ButtonGroup used for grouping buttons
 * we want to group together and manage their selected state
 */
const ButtonGroup = <T,>({
  className,
  style,
  children,
  variant = VARIANTS.SECONDARY,
  size = SIZES.MEDIUM,
  disabled,
  value,
  onChange,
  ...props
}: Omit<StylableComponent<HTMLDivElement>, 'onChange' | 'value'> & {
  variant?: (typeof VARIANTS)[keyof typeof VARIANTS];
  size?: (typeof SIZES)[keyof typeof SIZES];
  disabled?: boolean;
  value?: T;
  onChange?: (newValue: T) => void;
}) => {
  const { tokens } = useTheme();
  const buttonGroupTokens = tokens?.component?.buttonGroup;
  const childTokens = buttonGroupTokens?.child;
  const [selected, setSelected] = useControlled(UNSET as T, value, onChange);
  const hasSelection = selected !== UNSET;

  if (React.Children.count(children) === 1) {
    return children as ReactElement;
  }

  return (
    <div
      role={'group'}
      className={className}
      css={[
        buttonGroupTokens?.container,
        variantSwitch({
          statement: variant,
          variants: {
            [VARIANTS.SECONDARY]: buttonGroupTokens?.secondary,
            [VARIANTS.TERTIARY]: [
              buttonGroupTokens?.tertiary,
              hasSelection && buttonGroupTokens?.tertiaryWithSelection,
            ],
          },
        }),
        style,
      ]}
      {...props}
    >
      {React.Children.map(getValidChildren(children), (child, position) => {
        const typedChild = child as ReactElement<ComponentProps<typeof Button>>;
        const childProps = child.props as ComponentProps<typeof Button>;

        const selectionValue = (childProps?.value || position) as T;
        const isSelected = selected === selectionValue;

        return React.cloneElement(typedChild, {
          ...childProps,
          selected: isSelected,
          onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            if (childProps.onClick) {
              childProps.onClick(e);
            }

            setSelected(selectionValue);
          },
          variant,
          size,
          disabled: disabled || childProps.disabled,
          style: [
            {
              '&:hover': {
                ...childTokens?.hover,
                ...(!isSelected && childTokens?.unselectedHover),
              },
              '&:focus': childTokens?.focus,
              '&:active': childTokens?.active,
              '&[disabled]': {
                '&:hover': childTokens?.disabled?.hover,
              },
            },
            variantSwitch({
              statement: variant,
              variants: {
                [VARIANTS.SECONDARY]: {
                  '&[disabled]': childTokens?.secondaryDisabled,
                },
                [VARIANTS.TERTIARY]: {
                  '&[disabled]': childTokens?.tertiaryDisabled,
                },
              },
            }),
            childProps.style,
          ],
        });
      })}
    </div>
  );
};

ButtonGroup.VARIANTS = VARIANTS;
ButtonGroup.SIZES = SIZES;
ButtonGroup.displayName = 'ButtonGroup';

ButtonGroup.propTypes = {
  /**  The component's children tabs */
  children: PropTypes.any,
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /**  The style variant of buttons, defaults to 'secondary' */
  variant: PropTypes.oneOf(Object.values(VARIANTS)),
  /**  The size of buttons, defaults to 'medium' */
  size: PropTypes.oneOf(Object.values(SIZES)),
  /** Disable buttons functionality */
  disabled: PropTypes.bool,
  /** value for controlled usage */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** onChange handler for controlled usage */
  onChange: PropTypes.func,
};

export default ButtonGroup;
